/**
 * THE DYNAMIC FORMULA, as the pricebook runs it:
 *
 *   (hours x labor rate) + (part cost x markup) + complexity + uplift
 *
 * This is the ONE place a document may compute that number, and it may compute
 * it in exactly one situation: the operator edited the service's own dynamic
 * configuration. A materialized dynamic service otherwise behaves like a FLAT
 * one — the price was already calculated, and the document carries it (see
 * SPEC-line-item-model, "A materialized dynamic service is FLAT on the
 * document").
 *
 * It must therefore agree, term for term, with `DynamicPricingService.preview`
 * in operations, which is what priced the service in the catalog. It did not:
 * the UPLIFT (the config's `surcharge`) was missing here, so a service the
 * pricebook priced at $216 — $100 labor + a $16 part + a $100 uplift on the
 * labor — came back from a reprice at $116 and the operator watched $100
 * disappear for having touched a line.
 *
 * Two rules from SPEC-dynamic-service-material-markup still hold, and are what
 * makes the formula's part term what it is:
 *
 *   A part contributes cost x qty x (1 + markup) to the calculated OFFER total.
 *   Markup and line placement are independent questions.
 *
 *   Line placement decides where that contribution is billed. An included part
 *   stays inside the service line; a separately billed part moves to its own
 *   line. The document total does not change merely because the presentation
 *   changed, and no contribution is ever charged twice.
 */

import { partAmounts, rollUpService, type ServicePart } from './service-rollup';
import type {
  DynamicServicePricingConfig,
  LaborRateTier,
  MaterializedServicePart,
  ServiceLaborLine,
} from '@/types/pricebook';

export interface DynamicServicePrice {
  labor: number;
  /** Every part's contribution to the calculated offer total. */
  partsContribution: number;
  /**
   * Of that, the amount moved out of the service line and onto part rows.
   */
  partsSeparatelyCharged: number;
  complexityAdjustment: number;
  /** The profit uplift — the formula's "+ surcharges" term. */
  surcharge: number;
  afterHoursAdjustment: number;
  /** What the SERVICE line itself sells for. */
  servicePrice: number;
  /** The service line plus the part rows — what the document adds up to. */
  documentTotal: number;
}

const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * The time a line actually spends in each tier, and what the tier charges for
 * it. Everything about a labor line's money is read off this, so "what does it
 * cost" and "what rate is it billing at" can never be answered from two
 * different readings of the same tiers.
 */
function laborLineSegments(
  minutes: number,
  tiers: LaborRateTier[],
): { minutes: number; rate: number }[] {
  return [...tiers]
    .sort((a, b) => a.fromMinute - b.fromMinute)
    .map((tier) => {
      const upper = tier.toMinute ?? minutes;
      return {
        minutes: Math.max(Math.min(minutes, upper) - tier.fromMinute, 0),
        rate: Math.max(tier.rate, 0),
      };
    })
    .filter((segment) => segment.minutes > 0);
}

/** Labor for one line, charged through whatever tiers its rate profile has. */
function tieredLabor(minutes: number, tiers: LaborRateTier[]): number {
  return laborLineSegments(minutes, tiers).reduce(
    (total, segment) => total + (segment.minutes / 60) * segment.rate,
    0,
  );
}

/**
 * Does this labor line bill at the rate ON THE LINE, rather than at the tiers
 * frozen onto it when the service was materialized?
 *
 * True for a FIXED line carrying a rate of its own: that rate is the operator's
 * to change, and `laborLineTiers` bills it. False for a PROFILE line, and false
 * for a legacy row stored before `rateSource` existed — neither has a rate of
 * its own, so the frozen tiers are the only account of what an hour is worth.
 *
 * Exported because it is also the answer to "is `resolvedTiers` still worth
 * SHOWING". The wizard printed the frozen figure under every labor line, so a
 * custom rate retyped to $2,000 sat above "Snapshot: $1,000.00/hr" — the price,
 * the cost and the margin panel had all moved, and the one line of text left
 * saying otherwise read as the whole screen having ignored the edit. One
 * predicate, so what is displayed as the rate and what is charged as the rate
 * cannot disagree again.
 */
export function billsAtLineRate(line: ServiceLaborLine): boolean {
  return line.rateSource === 'FIXED' && Number.isFinite(line.fixedRate);
}

/**
 * The rate structure ONE labor line bills through.
 *
 * A PROFILE line bills through `resolvedTiers` — the profile's tiers as they
 * stood when the service was materialized onto this document, so repricing the
 * profile in the pricebook afterwards cannot move a price already quoted.
 *
 * A FIXED line bills through the rate ON THE LINE. Its `resolvedTiers` is only
 * that same rate frozen at materialization, and preferring it made "Rate / hour"
 * — the field the configuration wizard offers precisely because a custom rate is
 * the operator's to change — a control nothing responded to: typing $2,000 over
 * a $1,000 snapshot left the price, the cost and the margin panel exactly where
 * they were, which is what the owner reported.
 *
 * Term for term with `DynamicPricingService.preview` in operations, the code
 * that actually bills: PROFILE resolves tiers, everything else is
 * `hours x fixedRate`. `laborLineAmount` reads the pricebook's own rows the same
 * way, so all three agree about what an hour costs.
 *
 * A line with no rate of its own falls back to whatever was frozen on it: a row
 * stored before `rateSource` existed carries no `fixedRate`, and costing it at
 * $0 would report a service as free.
 */
function laborLineTiers(line: ServiceLaborLine): LaborRateTier[] {
  const fixedRate = Number.isFinite(line.fixedRate) ? Math.max(line.fixedRate as number, 0) : null;
  if (billsAtLineRate(line) && fixedRate != null) {
    return [{ fromMinute: 0, toMinute: null, rate: fixedRate }];
  }
  if (line.resolvedTiers?.length) return line.resolvedTiers;
  return [{ fromMinute: 0, toMinute: null, rate: fixedRate ?? 0 }];
}

export function dynamicServiceLabor(
  config: DynamicServicePricingConfig | null | undefined,
): number {
  return (config?.laborLines ?? []).reduce(
    (sum, line) => sum + tieredLabor(Math.max(line.durationMinutes, 0), laborLineTiers(line)),
    0,
  );
}

/**
 * THE TIME A MATERIALIZED DYNAMIC SERVICE IS PRICED FROM, and what that time is
 * worth — read off the labor lines, which is where a materialized service keeps
 * it.
 *
 * A dynamic service carries NO scalar `laborRate`: its rates live per labor
 * line, at possibly several, and `estimatedHours` is only their total, carried
 * so the server accepts the line. Reading that empty field as the line's hourly
 * rate is what printed "1 hrs @ $0.00/hr" under a service quoted at $1,699 —
 * the estimate stating that an hour of the work it is selling is worth nothing.
 *
 * `hourlyRate` is stated ONLY where there is one rate to state: every hour on
 * the line billing at the same figure. Lines at different rates, or one running
 * long into a second tier, have no single "per hour" — averaging them would
 * invent a rate no tier charges — so the caller is handed the hours and the
 * money and says that instead.
 *
 * Priced by the same `tieredLabor` that prices the service, so the basis shown
 * to the operator and the money in the price cannot drift apart.
 *
 * ONLY AN ENABLED CONFIGURATION ANSWERS — the same bar `isMaterializedDynamicService`
 * sets, and the reason this one function gates where `dynamicServiceLabor` and
 * `dynamicServicePrice` beside it do not: those are handed a configuration the
 * caller has already established is the line's, and this is asked OF a line, to
 * find out whether it has one. A disabled config is a switched-off leftover
 * carrying stale labor rows — a service configured dynamically and then set back
 * to flat keeps it — and reading it would state a basis the line is not priced
 * on, over the live `laborRate x hours` that it is.
 */
export interface DynamicLaborBasis {
  hours: number;
  /** What the labor term of the formula contributes to the price. */
  amount: number;
  /** The one rate every hour bills at, or `null` when there is more than one. */
  hourlyRate: number | null;
}

export function dynamicLaborBasis(
  config: DynamicServicePricingConfig | null | undefined,
): DynamicLaborBasis | null {
  if (config?.enabled !== true) return null;

  const segments = (config.laborLines ?? []).flatMap((line) =>
    laborLineSegments(Math.max(line.durationMinutes, 0), laborLineTiers(line)),
  );
  if (!segments.length) return null;

  const minutes = segments.reduce((total, segment) => total + segment.minutes, 0);
  const amount = segments.reduce(
    (total, segment) => total + (segment.minutes / 60) * segment.rate,
    0,
  );
  const rates = new Set(segments.map((segment) => round(segment.rate)));

  return {
    hours: minutes / 60,
    amount: round(amount),
    hourlyRate: rates.size === 1 ? [...rates][0]! : null,
  };
}

/**
 * What ONE of a snapshotted part adds to the calculated price.
 *
 * `unitAmount` is the stored form. The first version of the snapshot stored the
 * TOTAL instead, so that is divided back down rather than read as a unit figure
 * — a 4-off part would otherwise quadruple the moment anything repriced.
 */
export function materializedUnitAmount(part: MaterializedServicePart): number {
  if (part.unitAmount != null && Number.isFinite(part.unitAmount)) return part.unitAmount;
  const total = Number.isFinite(part.amount) ? (part.amount as number) : 0;
  return part.quantity > 0 ? total / part.quantity : total;
}

/** What a snapshotted part contributes in total. */
export function materializedPartAmount(part: MaterializedServicePart): number {
  return round(materializedUnitAmount(part) * Math.max(part.quantity, 0));
}

/**
 * THE PARTS THE FORMULA IS RUN OVER: the service's own snapshot, never the
 * document's part lines.
 *
 * The lines are the operator's — they can be repriced, re-quantified and
 * deleted, and none of that says anything about what the service was configured
 * to contain. Reading them back into the formula is exactly the leak the owner
 * reported: a filter repriced to $1,000 in the Materials section turned up
 * inside the service's configuration and, on the next reprice, inside its price.
 *
 * `null` ONLY where the field is absent — a service materialized before the
 * snapshot existed, or one built by hand — and the caller falls back to the
 * lines, which are then the only account of its parts there has ever been.
 *
 * An EMPTY snapshot is not that. `[]` is a materialized service stating a fact:
 * it was configured with no parts at all. Collapsing it to `null` sent a
 * labor-only service back to reading its document lines, so a material the
 * operator attached to it by hand was priced into the service the next time the
 * labor configuration was edited — the very leak above, reached from the other
 * side. `EstimateLineItemsSection` already reads the field with `??` and so
 * already treats `[]` as an answer; this is the two agreeing.
 */
export function materializedServiceParts(
  config: DynamicServicePricingConfig | null | undefined,
): ServicePart[] | null {
  const parts = config?.materializedParts;
  if (!parts) return null;
  return parts.map((part) => ({
    type: part.type,
    // The contribution is already resolved — cost and markup were applied when
    // the catalog priced it — so it travels as a per-unit price with no markup
    // to re-apply. `partAmounts` multiplies it by the quantity and stops there.
    basePrice: materializedUnitAmount(part),
    quantity: Math.max(part.quantity, 0),
    // Says nothing about the part's own row, which is the document line's
    // business. Here it only keeps the contribution out of the "charged again"
    // tally the formula reports.
    pricingTreatment: 'included_in_service',
  }));
}

/**
 * The snapshotted part amounts, placed on the document's current lines.
 *
 * The snapshot owns quantity and value: editing a material row must not rewrite
 * what the service was configured to contain. The linked row owns presentation:
 * whether that same value stays in the service line or moves onto its own line,
 * and whether an optional row is currently selected. Keeping those concerns
 * separate lets line placement change without recalculating the offer.
 */
export function documentServiceParts(
  config: DynamicServicePricingConfig | null | undefined,
  partLines: ServicePartLine[],
): ServicePart[] {
  const snapshot = materializedServiceParts(config);
  if (!snapshot) return partLines;

  return snapshot.map((part, index) => {
    const entry = config?.materializedParts?.[index];
    const line = entry ? linkedPartLine(partLines, entry) : undefined;
    return line
      ? {
          ...part,
          pricingTreatment: line.pricingTreatment,
          chargeable: line.chargeable,
          isSelected: line.isSelected,
        }
      : part;
  });
}

/** A document part LINE, as the configuration reads one. */
export type ServicePartLine = ServicePart & {
  /** The catalog item, so the entry can be matched back to its row. */
  lineItemId?: string;
  name?: string;
};

/**
 * A part LINE stated as a CONFIGURATION entry — what it is worth to the service
 * carrying it, per unit.
 *
 * `partAmounts` and not the line's `basePrice`: a catalog price is typed by hand
 * and is not reliably cost x (1 + markup), so reading it here would state a
 * figure that cannot be added up to the parts total the same function produces
 * when the service is priced. This is exactly the reading `dynamicServicePrice`
 * takes of a part line when there is no snapshot, so the two agree by
 * construction — a part added to the configuration is worth what the formula
 * would have said it was worth.
 */
/**
 * The precision a quantity is STORED at — `Decimal(14,4)` in Postgres.
 *
 * Shared by everything that derives one quantity from another, because binary
 * floating point does not divide cleanly: `6 x (1 / 3)` is 1.9999999999999998,
 * and a field the operator types whole numbers into must not echo that back.
 * Noise removal, deliberately NOT quantization — fractional part quantities are
 * real here (feet of pipe, pounds of refrigerant).
 */
export function roundQuantity(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

export function materializedPartFromLine(
  line: ServicePartLine,
  serviceQuantity = 1,
): MaterializedServicePart {
  const lineQuantity = Math.max(line.quantity ?? 0, 0);
  const contribution = partAmounts(line).sellingContribution;
  return {
    itemId: line.lineItemId,
    name: line.name ?? '',
    type: line.type,
    // DIVIDED BACK DOWN to what ONE delivery of the service consumes. A
    // document line's quantity is absolute — it already has the parent's count
    // multiplied through it (`expandComponentLines` in operations) — and this
    // record's format promises per-service. Copying the line's figure across
    // stored an absolute number in a per-service field, and every later edit
    // then multiplied the parent's count in a second time (NOT-633).
    quantity: roundQuantity(lineQuantity / Math.max(1, serviceQuantity)),
    // NOT divided. This is what one unit of the PART is worth, and that does not
    // depend on how many of the service are sold — `contribution` is the whole
    // line's, so it is the ABSOLUTE quantity that reduces it to a unit. The two
    // fields answer different questions and taking the same divisor to both
    // would halve the part's price as well as its count.
    unitAmount: lineQuantity > 0 ? contribution / lineQuantity : contribution,
  };
}

/**
 * WHAT THE SERVICE IS CONFIGURED TO CONTAIN, as a list that can be written back.
 *
 * The snapshot where there is one, and the service's part LINES where there is
 * not — the same fallback `materializedServiceParts` takes, and for the same
 * reason: a service materialized before the snapshot existed, or built by hand,
 * has no other account of its parts. An EMPTY snapshot is an answer ("configured
 * with no parts") and is never collapsed into the fallback.
 *
 * Every entry comes back with its `unitAmount` resolved, so anything written
 * from this list carries the unit figure rather than the total the first version
 * of the snapshot stored — a 4-off part would otherwise quadruple on the next
 * reprice.
 *
 * The ONE derivation of that list, shared by the wizard that draws it and the
 * state hook that writes it: an entry is addressed by its position, so a second
 * implementation drifting by one line is a part edited in place of another.
 */
export function configuredServiceParts(
  config: DynamicServicePricingConfig | null | undefined,
  partLines: ServicePartLine[],
  serviceQuantity = 1,
): MaterializedServicePart[] {
  const snapshot = config?.materializedParts;
  if (snapshot) {
    // Already per-service — that is what the snapshot format stores, and the
    // service's own quantity has no business here.
    return snapshot.map((part) => ({ ...part, unitAmount: materializedUnitAmount(part) }));
  }
  // The fallback reads DOCUMENT ROWS, whose quantities are absolute, so they are
  // converted on the way into a list whose entries are per-service. Both meanings
  // used to reach the wizard through this one return, which is what let a legacy
  // service's absolute count be edited as though it were per-delivery.
  return partLines.map((line) => materializedPartFromLine(line, serviceQuantity));
}

/**
 * The document ROW a configured part was expanded onto — where there is one, and
 * where there is no doubt which.
 *
 * Matched on the catalog id, and on the NAME where the entry has no id: a part
 * typed by hand has no catalog row, and the name is what the configuration
 * copied off the line. Only a line that is ITSELF hand-typed can answer that
 * way, so a catalog row is never claimed by a coincidence of naming.
 *
 * Either way the match must be UNIQUE. The pricebook cannot hold the same item
 * twice under one service — `@@unique([parentItemId, childItemId])` — so the
 * snapshot's ids are distinct; the DOCUMENT's need not be, because a part can
 * also be attached by hand and the rows can be reordered. With two candidates
 * there is no fact about which is "the" configured line, and acting on an
 * arbitrary one is a guess.
 *
 * The ONE answer to "which row is this part", so the quantity that carries to a
 * line and the line that goes when the part does cannot pick different rows.
 */
export function linkedPartLine<T extends ServicePartLine>(
  partLines: T[],
  part: MaterializedServicePart,
): T | undefined {
  const matches = part.itemId
    ? partLines.filter((line) => line.lineItemId === part.itemId)
    : partLines.filter((line) => !line.lineItemId && line.name === part.name);
  return matches.length === 1 ? matches[0] : undefined;
}

export function dynamicServicePrice(args: {
  config: DynamicServicePricingConfig | null | undefined;
  parts: ServicePart[];
}): DynamicServicePrice {
  const { config, parts } = args;
  const labor = dynamicServiceLabor(config);

  // The one function that owns "what is a part worth to its service" — so the
  // relationship markup, the declined optional and the uplift base are answered
  // once rather than re-derived per surface.
  //
  // The uplift is charged on labor and the parts' contribution and never
  // compounds onto complexity, which is the order `DynamicPricingService`
  // charges it in; complexity is added below, outside the base it is charged on.
  const rollup = rollUpService({ labor, parts, surcharge: config?.surcharge });
  const partsContribution = rollup.partsSellingContribution;

  // Complexity and after-hours are charged on the whole job — labor plus every
  // part, whichever way each part is billed. Which part bills separately only
  // decides where its money is presented, never how big the job was.
  const base = labor + partsContribution;
  const complexityAdjustment = base * (Math.max(config?.complexityAdjustmentPercent ?? 0, 0) / 100);
  const afterHoursAdjustment =
    config?.materializedAfterHours && config.afterHours?.enabled
      ? config.afterHours.adjustmentType === 'FLAT'
        ? Math.max(config.afterHours.value, 0)
        : (base + complexityAdjustment) * (Math.max(config.afterHours.value, 0) / 100)
      : 0;

  // The formula first calculates the offer as a whole. Line placement happens
  // afterwards: any part billed separately is removed from the service line
  // and carried by its own line instead. This keeps the offer total stable and
  // prevents the same marked-up contribution from being charged twice.
  const calculatedTotal = Math.max(
    0,
    base +
      complexityAdjustment +
      rollup.surcharge +
      afterHoursAdjustment +
      (config?.materializedFixedAdjustment ?? 0),
  );
  const servicePrice = Math.max(0, calculatedTotal - rollup.partsSeparatelyCharged);

  return {
    labor: round(labor),
    partsContribution: round(partsContribution),
    partsSeparatelyCharged: rollup.partsSeparatelyCharged,
    complexityAdjustment: round(complexityAdjustment),
    surcharge: rollup.surcharge,
    afterHoursAdjustment: round(afterHoursAdjustment),
    servicePrice: round(servicePrice),
    documentTotal: round(servicePrice + rollup.partsSeparatelyCharged),
  };
}

/**
 * The two adjustments a dynamic service carries but does NOT normally charge.
 *
 * After-hours and the add-on discount are CONDITIONAL: they apply to a
 * particular job, when the work runs out of hours or when the service is sold
 * alongside another. The catalog's summary shows the everyday price, so neither
 * is included — which left the operator configuring a rule they could not see
 * the effect of, and having to sell a job to find out.
 *
 * This is preview only. Nothing here is saved, and the everyday price is
 * unchanged: `materializedAfterHours` on a document line is still what decides
 * whether a real estimate charges it.
 *
 * The order is the biller's order, not a convenience: after-hours is added to
 * the price, and the add-on discount comes off the total INCLUDING it. Applying
 * the discount first would discount a smaller number and undercharge every
 * after-hours add-on. Lockstep with `dynamic-pricing.service` in operations,
 * which is the code that actually bills.
 */
export interface OptionalAdjustmentPreview {
  afterHoursAdjustment: number;
  addOnDiscount: number;
  /** The price with whichever adjustments were asked for. */
  total: number;
}

export function previewOptionalAdjustments(args: {
  /** The everyday calculated price — labor, parts, complexity, uplift. */
  price: number;
  config: DynamicServicePricingConfig | null | undefined;
  /** Charge the after-hours adjustment. */
  afterHours?: boolean;
  /** Sell this service as an add-on to another. */
  asAddOn?: boolean;
}): OptionalAdjustmentPreview {
  const { price, config } = args;

  const after = config?.afterHours;
  const afterHoursAdjustment =
    args.afterHours && after?.enabled
      ? after.adjustmentType === 'FLAT'
        ? Math.max(after.value, 0)
        : (price * Math.max(after.value, 0)) / 100
      : 0;

  const beforeAddOn = price + afterHoursAdjustment;

  const addOn = config?.addOnDiscount;
  const addOnDiscount =
    args.asAddOn && addOn?.enabled
      ? // Never more than the price. A discount larger than the job would
        // otherwise pay the customer to take it.
        Math.min(
          beforeAddOn,
          addOn.discountType === 'FLAT'
            ? Math.max(addOn.value, 0)
            : (beforeAddOn * Math.max(addOn.value, 0)) / 100,
        )
      : 0;

  return {
    afterHoursAdjustment: round(afterHoursAdjustment),
    addOnDiscount: round(addOnDiscount),
    total: round(beforeAddOn - addOnDiscount),
  };
}
